import type { ActivityTypeLabel, BookingDetail, BookingShipmentActivity } from '@dc-trans/data-layer';
import { useEffect, useMemo, useState } from 'react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { CheckCircle2Icon, CircleArrowUpIcon, CircleDotIcon, CircleEllipsisIcon, CircleIcon } from 'lucide-react';
import dayjs from 'dayjs';
import { uniqBy } from 'es-toolkit';
import { Button, Select, Steps } from 'pink-lava-ui';
import ExpandableContainer from '@/components/ui/ExpandableContainer';
import { Divider } from 'antd';
import ConditionalTooltip from '@/components/ConditionalTooltip';

const BookingRouteStatus = ({
  booking,
  activityList,
}: {
  booking: BookingDetail;
  activityList: BookingShipmentActivity[];
}) => {
  const [currentRoute, setCurrentRoute] = useState<string>(() => booking?.routes?.[0]?.destination_ulid);
  const { isXlAndUp } = useBreakpoint();

  const isAssigned = !!booking.assignment?.vendor_vehicle?.ulid;

  // Keep current route in sync with booking
  useEffect(() => {
    setCurrentRoute(booking?.routes?.[0]?.destination_ulid);
  }, [booking]);

  // Map first activity per type for the selected route
  const mappedActivity = useMemo(() => {
    if (!activityList?.length) return {} as Record<keyof typeof ActivityTypeLabel, BookingShipmentActivity>;

    const routeAgnosticTypes = new Set<keyof typeof ActivityTypeLabel>([
      'new',
      'heading_to_pickup',
      'arrived_at_pickup',
      'on_the_way',
    ]);

    const filtered = activityList.filter(item => {
      if (item.activity_type === 'transit') return false;

      const activityType = item.activity_type as keyof typeof ActivityTypeLabel;

      if (routeAgnosticTypes.has(activityType)) return true;

      return Boolean(currentRoute) && item.destination_ulid === currentRoute;
    });

    return filtered.reduce(
      (acc, item) => {
        // Only take the first occurrence for each activity_type
        if (!(acc as any)[item.activity_type]) {
          (acc as any)[item.activity_type] = item;
        }
        return acc;
      },
      {} as Record<keyof typeof ActivityTypeLabel, BookingShipmentActivity>
    );
  }, [activityList, currentRoute]);

  // Compute current steps (main and nested) from mapped activities
  const { mainCurrent, nestedCurrent } = useMemo(() => {
    const has = (k: keyof typeof ActivityTypeLabel) => Boolean((mappedActivity as any)[k]);

    const main = has('proof_of_delivery')
      ? 3
      : has('arrival_claim')
        ? 2
        : has('heading_to_pickup') || has('arrived_at_pickup') || has('on_the_way') || has('arrived_at_destination')
          ? 1
          : 0;

    const nested = has('arrival_claim')
      ? 4
      : has('arrived_at_destination')
        ? 3
        : has('on_the_way')
          ? 2
          : has('arrived_at_pickup')
            ? 1
            : has('heading_to_pickup')
              ? 0
              : 0;

    return { mainCurrent: main, nestedCurrent: nested };
  }, [mappedActivity]);

  // Step icon based on completion/current state
  const getStepIcon = (isCompleted: boolean, isCurrent: boolean) => {
    return isCompleted ? (
      <CheckCircle2Icon strokeWidth={3} size={24} className="text-blue-400" />
    ) : isCurrent ? (
      <CircleEllipsisIcon strokeWidth={3} size={24} className="text-orange-300" />
    ) : (
      <CircleIcon strokeWidth={3} size={24} className="text-gray-300" />
    );
  };

  // Reusable subtitle renderer for activities
  const getActivitySubtitle = (activityType: keyof typeof ActivityTypeLabel) => {
    const activity = (mappedActivity as any)[activityType] as BookingShipmentActivity | undefined;

    return (
      <div className="w-full">
        <div className="text-sm text-gray-500">
          Nomor Polisi Kendaraan : {activity?.vendor_vehicle_license_plate || '-'}
        </div>
        <div className="text-sm text-gray-500">Nama Pengemudi : {activity?.driver_name || '-'}</div>
        <div className="text-sm text-gray-500">
          Waktu Update : {activity ? dayjs(activity.activity_time).format('DD/MM/YYYY HH:mm') : '-'}
        </div>
        {activity?.latitude && activity?.longitude && (
          <Button
            size="small"
            type="ghost"
            className="px-0! py-0!"
            onClick={() => {
              window.open(`https://www.google.com/maps/search/?api=1&query=${activity.latitude},${activity.longitude}`);
            }}
          >
            Lihat Lokasi
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="flex font-nunito flex-col bg-white rounded-2xl shadow-[0px_4px_16px_2px_rgba(170,170,170,0.15)]">
      <p className="text-lg font-semibold px-4 pt-4">Delivery Tracking</p>
      <BookingRouteLocation booking={booking} currentRoute={currentRoute} setCurrentRoute={setCurrentRoute} />
      <div className="p-4 pt-2">
        <ExpandableContainer disabled={isXlAndUp} minimizedHeight={64}>
          <Steps
            direction="vertical"
            size="small"
            current={mainCurrent}
            items={[
              {
                title: 'Baru',
                icon: getStepIcon(isAssigned || mainCurrent > 0, mainCurrent === 0),
                subTitle: getActivitySubtitle('new'),
              },
              {
                title: 'Sedang Berlangsung',
                icon: getStepIcon(mainCurrent > 1, mainCurrent === 1),
                subTitle: getActivitySubtitle('heading_to_pickup'),
                description: (
                  <Steps
                    size="small"
                    direction="vertical"
                    current={nestedCurrent}
                    items={[
                      {
                        title: 'Tiba di Titik Pengambilan',
                        icon: getStepIcon(
                          nestedCurrent > 0 || mainCurrent > 2,
                          nestedCurrent === 0 && mainCurrent === 1
                        ),
                        subTitle: getActivitySubtitle('arrived_at_pickup'),
                      },
                      {
                        title: 'Dalam Perjalanan',
                        icon: getStepIcon(nestedCurrent > 1 || mainCurrent > 2, nestedCurrent === 1),
                        subTitle: getActivitySubtitle('on_the_way'),
                      },
                      {
                        title: 'Tiba di Tujuan',
                        icon: getStepIcon(nestedCurrent > 2 || mainCurrent > 2, nestedCurrent === 2),
                        subTitle: getActivitySubtitle('arrived_at_destination'),
                      },
                      {
                        title: 'Arrival Claim',
                        icon: getStepIcon(nestedCurrent > 3 || mainCurrent > 2, nestedCurrent === 3),
                        subTitle: getActivitySubtitle('arrival_claim'),
                      },
                    ]}
                  />
                ),
              },
              {
                title: 'Bukti Penerimaan Diterima',
                icon: getStepIcon(mainCurrent > 2, mainCurrent === 2),
                subTitle: getActivitySubtitle('proof_of_delivery'),
              },
            ]}
          />
        </ExpandableContainer>
      </div>
    </div>
  );
};

export const BookingRouteLocation = ({
  booking,
  currentRoute,
  setCurrentRoute,
}: {
  booking: BookingDetail;
  currentRoute: string;
  setCurrentRoute: (_value: string) => void;
}) => {
  const routeOptions = useMemo(
    () =>
      uniqBy(booking.routes, item => item.destination_ulid)?.map(item => ({
        label: item.destination_name,
        value: item.destination_ulid,
      })) ?? [],
    [booking.routes]
  );

  return (
    <div className="border-1 border-gray-200 py-1 px-3.5 mx-4 my-2 rounded-xl">
      <div className="flex flex-row items-center gap-3 overflow-hidden">
        <CircleArrowUpIcon strokeWidth={3} size={18} className="text-green-500" />
        <ConditionalTooltip
          placement="top"
          title={booking.routes?.[0]?.origin_name || ''}
          className=" flex-1 h-[36px] font-semibold leading-[36px] truncate"
        >
          {booking.routes?.[0]?.origin_name}
        </ConditionalTooltip>
      </div>
      <div className="ml-[30px]!">
        <Divider size="small" className="my-1!" />
      </div>
      <div className="flex flex-row items-center gap-3">
        <CircleDotIcon strokeWidth={3} size={18} className="text-red-500" />
        <div className="flex-1 overflow-hidden [&_.ant-select-selector]:pl-0! [&_.ant-select-selector]:pr-2!">
          {/* <div className="text-sm font-semibold px-4 pt-2">Pilih Tujuan Pengiriman</div> */}
          <Select
            className="w-full"
            placeholder="Pilih Tujuan"
            variant="borderless"
            size="small"
            allowSearch
            labelRender={({ label }) => (
              <ConditionalTooltip
                placement="bottom"
                title={label?.toString()!}
                className="font-semibold truncate w-full"
              >
                {label}
              </ConditionalTooltip>
            )}
            onChange={setCurrentRoute}
            value={currentRoute}
            defaultActiveFirstOption
            options={routeOptions}
            filterOption={(input, option) => {
              return ((option!.label as string) ?? '').toLowerCase().includes(input.toLowerCase());
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default BookingRouteStatus;
